import { t } from 'i18next';
import Lottie from 'lottie-react';
import { lazy, LazyExoticComponent, Suspense } from 'react';
import {
  createBrowserRouter,
  Navigate,
  RouterProvider
} from 'react-router-dom';

import AppLoader from '~assets/loader.json';
import { type AppImplementationConfig, useLocalStorage } from '~hooks/index';
import { DocumentProvider } from '~layouts/PatientResultLayout/contexts/DocumentContext';
import {
  PrivateRoute,
  privateRouteOptions,
  PublicRoute,
  ROUTES
} from './index';

const LandingPage = lazy(() => import('~pages/Public/LandingPage/LandingPage'));
const ResultsLogin = lazy(
  () => import('~pages/Public/ResultsLogin/ResultsLogin')
);

const LoginPage = lazy(() => import('~pages/Public/LoginPage/LoginPage'));
const Error404 = lazy(() => import('~pages/Error404/Error404'));
const Redirect = lazy(() => import('~pages/Public/Redirect/Redirect'));

const Home = lazy(() => import('~pages/Private/Home/Home'));
const CreateWorkList = lazy(
  () => import('~pages/Private/CreateWorkList/CreateWorkList')
);
const Dashboard = lazy(() => import('~pages/Private/Dashboard/Dashboard'));
const FileLoad = lazy(
  () => import('~pages/Private/MassiveLoad/FileLoad/FileLoad')
);
const PatientList = lazy(
  () => import('~pages/Private/PatientList/PatientList')
);
const PresignedLoad = lazy(
  () => import('~pages/Private/PresignedLoad/PresignedLoad')
);
const FileHistory = lazy(
  () => import('~pages/Private/MassiveLoad/FileHistory/FileHistory')
);
const ObservationZone = lazy(
  () => import('~pages/Private/ObservationZone/Rooms/ObservationZone')
);
const LocationWithPatients = lazy(
  () =>
    import('~pages/Private/ObservationZone/LocationWithPatients/LocationWithPatients')
);
const WorkUnit = lazy(() => import('~pages/Private/WorkUnit/WorkUnit'));
const GenericCohort = lazy(
  () => import('~layouts/Cohort/GenericCohort/GenericCohort')
);
const Users = lazy(() => import('~pages/Private/Users/Users'));
const UserProfile = lazy(
  () => import('~pages/Private/UserProfile/UserProfile')
);
const UserManagementForm = lazy(
  () => import('~pages/Private/Users/UserManagementForm/UserManagementForm')
);
const GroupsManagement = lazy(
  () => import('~pages/Private/Users/GroupsManagement/GroupsManagement')
);
const OrdersAssigned = lazy(
  () => import('~pages/Private/OrderAssignment/OrderAssignment')
);
const OrderAssignmentForm = lazy(
  () =>
    import('~pages/Private/OrderAssignment/OrderAssignmentForm/OrderAssignmentForm')
);
const FlowCreation = lazy(
  () => import('~pages/Private/FlowCreation/FlowCreation')
);

const PatientResults = lazy(
  () => import('~implementations/fci/pages/PatientResults/PatientResults')
);

const PatientHistory = lazy(
  () =>
    import('~implementations/fci/pages/PatientResults/PatientHistory/PatientHistory')
);
//* Private routes from implementations
const FciSchedule = lazy(
  () => import('~implementations/fci/pages/Schedule/Schedule')
);
const FciCohort = lazy(
  () => import('~implementations/fci/pages/Cohorts/FciCohorts')
);
const FciControlTower = lazy(
  () => import('~implementations/fci/pages/ControlTower/ControlTower')
);
const HorizonteControlTower = lazy(
  () => import('~implementations/horizonte/pages/ControlTower/ControlTower')
);
const RimacCohort = lazy(
  () => import('~implementations/rimac/pages/Cohorts/RimacCohorts')
);
const HorizonteCohort = lazy(
  () => import('~implementations/horizonte/pages/Cohorts/RimacCohorts')
);
const HorizonteCohortGrouped = lazy(
  () => import('~implementations/horizonte/pages/CohortsGrouped/FciCohorts')
);
const Reports = lazy(() => import('~pages/Private/Reports/Reports'));
const CohortAssign = lazy(
  () => import('~pages/Private/CohortAssign/CohortAssign')
);
const ColManagePatients = lazy(
  () =>
    import('~implementations/colsubsidio/pages/ManagePatients/ManagePatients')
);
const ColSchedulePatients = lazy(
  () =>
    import('~implementations/colsubsidio/pages/ManagePatients/SchedulePatients/SchedulePatients')
);
const ColApplications = lazy(
  () => import('~implementations/colsubsidio/pages/Applications/Applications')
);
const ColPatientConfirmed = lazy(
  () =>
    import('~implementations/colsubsidio/pages/PatientConfirmed/PatientConfirmed')
);
const ColCalculator = lazy(
  () => import('~implementations/colsubsidio/pages/Calculator/Calculator')
);

const ColProgrammingAssistant = lazy(
  () =>
    import('~implementations/colsubsidio/pages/ProgrammingAssistant/ProgrammingAssistant')
);
const ColBilling = lazy(
  () => import('~implementations/colsubsidio/pages/Billing/Billing')
);
const ColRoomManager = lazy(
  () => import('~implementations/colsubsidio/pages/RoomManager/RoomManager')
);
const ColEducation = lazy(
  () => import('~implementations/colsubsidio/pages/Education/Education')
);
const CoosaludCohort = lazy(
  () => import('~implementations/coosalud/pages/Cohorts/Rimac/RimacCohorts')
);
const CoosaludCohortGrouped = lazy(
  () => import('~implementations/coosalud/pages/Cohorts/Fci/FciCohorts')
);
const CoosaludControlTower = lazy(
  () => import('~implementations/coosalud/pages/ControlTower/ControlTower')
);
const ColCohort = lazy(
  () => import('~implementations/colsubsidio/pages/ColCohorts/FciCohorts')
);
const MamaPresumptiveRoute = lazy(
  () =>
    import('~implementations/sanitas/pages/MamaPresumptiveRoute/MamaPresumptiveRoute')
);
const SanitasCohort = lazy(
  () => import('~implementations/sanitas/pages/Cohorts/FciCohorts')
);
const KhymeiCohorts = lazy(
  () => import('~implementations/khymei/pages/Cohorts/KhymeiCohorts')
);
const CohortV2 = lazy(() => import('~pages/Private/CohortV2/CohortV2'));
const HorizonteActivities = lazy(
  () =>
    import('~implementations/horizonte/pages/HorizonteActivities/HorizonteActivities')
);

const CoomevaFileLoad0256 = lazy(
  () => import('~implementations/coomevamp/pages/FileLoad0256/FileLoad0256')
);

const CoomevaFileUpload = lazy(
  () => import('~implementations/coomevamp/pages/FileUpload/FileUpload')
);

const CoomevaFileUploadOpportunity = lazy(
  () =>
    import('~implementations/coomevamp/pages/FileUploadOpportunity/FileUploadOpportunity')
);
const RoutesDirectory = () => {
  const { userData } = useLocalStorage();

  const appPageDirectory: Record<
    string,
    LazyExoticComponent<
      ({
        urlPath
      }: {
        urlPath?: string;
        cohortId?: number;
        implementation?: AppImplementationConfig['name'];
      }) => JSX.Element
    >
  > = {
    //* Here will be the common components for all implementations
    cohort: GenericCohort,
    create_worklist: CreateWorkList,
    dashboard: Dashboard,
    file_history: FileHistory,
    file_load: FileLoad,
    observation_zone: ObservationZone,
    patient_list: PatientList,
    users: Users,
    work_unit: WorkUnit,
    reports: Reports,
    cohort_assign: CohortAssign,
    presigned_load: PresignedLoad,
    orders_assigned: OrdersAssigned,
    flow_creation: FlowCreation,
    //* Here will be the components used by the implementations
    fci_cohort: FciCohort,
    fci_control_tower: FciControlTower,
    fci_schedule: FciSchedule,
    rimac_cohort: RimacCohort,
    horizonte_cohort: HorizonteCohort,
    horizonte_cohort_grouped: HorizonteCohortGrouped,
    horizonte_control_tower: HorizonteControlTower,
    col_manage_patients: ColManagePatients,
    col_applications: ColApplications,
    col_patient_confirmed: ColPatientConfirmed,
    col_programming_assistant: ColProgrammingAssistant,
    col_billing: ColBilling,
    col_room_manager: ColRoomManager,
    col_education: ColEducation,
    col_cohort: ColCohort,
    col_calculator: ColCalculator,
    coomeva_file_load_0256: CoomevaFileLoad0256,
    coomeva_file_upload: CoomevaFileUpload,
    coomeva_file_upload_opportunity: CoomevaFileUploadOpportunity,
    coosalud_cohort: CoosaludCohort,
    coosalud_cohort_grouped: CoosaludCohortGrouped,
    coosalud_control_tower: CoosaludControlTower,
    sanitas_mama_presumptive_route: MamaPresumptiveRoute,
    sanitas_cohort: SanitasCohort,
    khymei_cohort: KhymeiCohorts,
    cohort_v2: CohortV2,
    horizonte_activities: HorizonteActivities
  };

  const pageSelected = (name: string) => {
    const Page = appPageDirectory[name];
    return <Page />;
  };

  const router = createBrowserRouter([
    {
      path: '*',
      element: <Navigate to={ROUTES.ERROR} />
    },
    {
      path: ROUTES.ERROR,
      element: <Error404 />
    },
    {
      path: ROUTES.ROOT,
      element: <PublicRoute />,
      children: [
        {
          index: true,
          element: <LandingPage />
        },
        {
          path: ROUTES.LOGIN,
          element: <LoginPage />
        },
        {
          path: ROUTES.REDIRECT,
          element: <Redirect />
        },
        {
          path: ROUTES.RESULTS_LOGIN,
          element: (
            <DocumentProvider>
              <ResultsLogin />
            </DocumentProvider>
          )
        },
        {
          path: ROUTES.RESULT_PATIENT,
          element: (
            <DocumentProvider>
              <PatientResults />
            </DocumentProvider>
          )
        },
        {
          path: ROUTES.RESULTS_HISTORY,
          element: (
            <DocumentProvider>
              <PatientHistory />
            </DocumentProvider>
          )
        }
      ]
    },
    {
      path: ROUTES.PLATFORM,
      element: <PrivateRoute />,
      children: [
        {
          path: ROUTES.PLATFORM,
          element: <Home />
        },
        {
          path: ROUTES.CREATE_LIST,
          element: (
            <>
              <h2 className="page_title">{t('routes.create_work_list')}</h2>
              <CreateWorkList />
            </>
          )
        },
        {
          path: ROUTES.USER_PROFILE,
          element: (
            <>
              <h2 className="page_title">{t('routes.user_profile')}</h2>
              <UserProfile />
            </>
          )
        },
        {
          path: ROUTES.USER_UPDATE,
          element: (
            <>
              <h2 className="page_title">{t('routes.update_user')}</h2>
              <UserManagementForm />
            </>
          )
        },
        {
          path: ROUTES.USER_UPDATE,
          element: (
            <>
              <h2 className="page_title">{t('routes.update_user')}</h2>
              <UserManagementForm />
            </>
          )
        },
        {
          path: ROUTES.USER_CREATE,
          element: (
            <>
              <h2 className="page_title">{t('routes.create_user')}</h2>
              <UserManagementForm />
            </>
          )
        },
        {
          path: ROUTES.GROUPS_MANAGEMENT,
          element: (
            <>
              <h2 className="page_title">{t('routes.groups')}</h2>
              <GroupsManagement />
            </>
          )
        },
        {
          path: ROUTES.LOCATION_WITH_PATIENTS,
          element: (
            <>
              <h2 className="page_title">
                {t('routes.location_with_patients')}
              </h2>
              <LocationWithPatients />
            </>
          )
        },
        {
          path: ROUTES.SCHEDULE_MANAGE,
          element: (
            <>
              <h2 className="page_title">{t('routes.schedule_patients')}</h2>
              <ColSchedulePatients />
            </>
          )
        },
        {
          path: ROUTES.ORDER_ASSIGNMENT,
          element: (
            <>
              <h2 className="page_title">{t('routes.order_assignment')}</h2>
              <OrderAssignmentForm />
            </>
          )
        },
        ...privateRouteOptions(userData.sidebarData).map((item) => {
          return {
            path: `/platform/${item.functional_name}`,
            element: (
              <>
                {!['work_unit', 'dashboard'].includes(item.functional_name!) ? (
                  <>
                    {/* TODO: Page title rendering pending */}
                    {/* <h2 className="page_title">{getLocalizedLabel(item, language)}</h2> */}
                    <div className="mt-3">
                      {pageSelected(item.functional_name!)}
                    </div>
                  </>
                ) : (
                  pageSelected(item.functional_name!)
                )}
              </>
            )
          };
        })
      ]
    }
  ]);

  return (
    <Suspense
      fallback={
        <div className="loadingContainer">
          <Lottie animationData={AppLoader} loop={true} />
        </div>
      }
    >
      <RouterProvider router={router} />
    </Suspense>
  );
};

export default RoutesDirectory;
